import React, { useState, useEffect } from 'react';
import { useMetadata } from '../../context/MetadataContext'; 
import { useGameState } from '../../context/GameStateContext';
import { useGlobalError } from '../../context/GlobalErrorContext';
import { useAllCreatedPuzzles } from '../../hooks/useAllCreatedPuzzles';
import { formatDateUppercase } from '../../utils/formatDate';
import CreatedPuzzleDetail from '../CreatedPuzzleDetail';


const CreateResults = () => {
  const [selectedPuzzle, setSelectedPuzzle] = useState(null);
  const { getText } = useMetadata();
  const { gameData, abandonPuzzle} = useGameState();
  const { triggerError } = useGlobalError();

  useEffect(() => {
    if (!gameData?.state) {
      triggerError('high', 'CreateResult component rendered when puzzle already selected. This indicates a routing/status determination error.');
    }
  }, [gameData?.state, triggerError]);

  // Created puzzles (fetched by the hook on mount)
  const { createdPuzzles, puzzlesLoading } = useAllCreatedPuzzles();

  const handleAnother = async () => {
    abandonPuzzle();
  };

  const handleRowClick = (puzzle) => {
    setSelectedPuzzle(puzzle);
  };

  const handleDetailClose = () => {
    setSelectedPuzzle(null);
  };

  return (
    <div className="dataContainer">
      <div className="containerHeader">
        <h2>{getText('create.title')}</h2>
      </div>
      <div className="dataContent">
        <div className="contentGroup">       
          <div className="groupHeader">
            <h3>{getText('create.result_title')}</h3>
          </div>
          
          <div className="groupDescription">
            {getText('create.result_description')}
          </div>
        </div>

        <div className="contentGroup">
          <div className="groupHeader">
            <h3>{getText('create.created_title')}</h3> 
          </div>
          
          {puzzlesLoading ? (
            <div className="groupDescription">
              {getText('common_text.loading')}
            </div>
          ) : createdPuzzles.length === 0 ? (
            <div className="groupDescription">
              {getText('create.no_puzzles')}
            </div>
          ) : (
            <div className="puzzleTableContainer">
              <table className="puzzleTable">
                <thead>
                  <tr>
                    <th>{getText('common_text.style') || 'Style'}</th>
                    <th>{getText('common_text.size') || 'Size'}</th>
                    <th>{getText('common_text.submitted') || 'Submitted'}</th>
                    <th>{getText('common_text.status') || 'Status'}</th>
                  </tr>
                </thead>
                <tbody className="puzzleTableBody">
                  {createdPuzzles.map((puzzle, index) => (
                    <tr 
                      key={index}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleRowClick(puzzle)}
                      onMouseEnter={(e) => e.target.closest('tr').style.backgroundColor = 'var(--background-color)'}
                      onMouseLeave={(e) => e.target.closest('tr').style.backgroundColor = ''}
                    >
                      <td className="puzzleStyleCell">
                        {puzzle.style.charAt(0).toUpperCase() + puzzle.style.slice(1)}
                      </td>
                      <td className="puzzleSizeCell">
                        {puzzle.size}x{puzzle.size}
                      </td>
                      <td className="puzzleDateCell">
                        {formatDateUppercase(puzzle.submitted_ts || puzzle.created_ts)}
                      </td>
                      <td>
                        {puzzle.status.charAt(0).toUpperCase() + puzzle.status.slice(1)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <div className="actionButtonContainer">
        <button 
          className="primaryButton"
          onClick={handleAnother}
        >
          {getText('create.another_button')}
        </button>
      </div>  

      {/* Render CreatedPuzzleDetail modal if a puzzle is selected */}
      {selectedPuzzle && (
        <div
          onClick={handleDetailClose}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(2px)'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--panel-background-color)',
              borderRadius: '12px',
              width: '90vw',
              maxWidth: '700px',
              maxHeight: '85vh',
              overflow: 'hidden',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <CreatedPuzzleDetail
              puzzleData={selectedPuzzle}
              onClose={handleDetailClose}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateResults;
